import React from "react";
import { supabase } from "@/integrations/supabase/client";

type LogRow = { id: string; user_id: string; segment: "30" | "60" | "90"; coupon_code: string | null; sent_at: string };

export function AdminWinBack({ lang, toast }: { lang: "es" | "en"; toast?: (m: string) => void }) {
  const t = (es: string, en: string) => (lang === "es" ? es : en);
  const [rows, setRows] = React.useState<LogRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [running, setRunning] = React.useState<"none" | "dry" | "live">("none");
  const [lastSummary, setLastSummary] = React.useState<any>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("win_back_log")
      .select("id,user_id,segment,coupon_code,sent_at")
      .order("sent_at", { ascending: false })
      .limit(500);
    if (!error && data) setRows(data as any);
    setLoading(false);
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const now = Date.now();
  const within = (days: number) => rows.filter(r => now - new Date(r.sent_at).getTime() <= days * 86400_000);
  const stats = React.useMemo(() => {
    const last30 = within(30);
    const bySeg = (segs: LogRow[]) => ({
      "30": segs.filter(r => r.segment === "30").length,
      "60": segs.filter(r => r.segment === "60").length,
      "90": segs.filter(r => r.segment === "90").length,
    });
    return {
      total: rows.length,
      last24h: within(1).length,
      last7d: within(7).length,
      last30d: last30.length,
      bySegmentTotal: bySeg(rows),
      bySegment30d: bySeg(last30),
    };
  }, [rows]);

  const run = async (dryRun: boolean) => {
    setRunning(dryRun ? "dry" : "live");
    setLastSummary(null);
    try {
      const { data, error } = await supabase.functions.invoke("win-back-campaign", { body: { dryRun } });
      if (error) throw error;
      setLastSummary(data);
      toast?.(dryRun
        ? t("Simulación completada", "Dry-run complete")
        : t("Campaña ejecutada", "Campaign executed"));
      if (!dryRun) await load();
    } catch (e: any) {
      toast?.(t("Error: ", "Error: ") + (e?.message ?? String(e)));
    } finally {
      setRunning("none");
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h3 style={{ margin: 0 }}>{t("Win-back / Recuperación", "Win-back campaigns")}</h3>
          <p style={{ margin: "4px 0 0", color: "#666", fontSize: 13 }}>
            {t(
              "Recupera clientes inactivos a 30, 60 y 90 días con cupones automáticos.",
              "Recover lapsed customers at 30, 60 and 90 days with auto coupons."
            )}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn" onClick={() => run(true)} disabled={running !== "none"}>
            {running === "dry" ? t("Simulando…", "Running…") : t("Simular (dry-run)", "Dry-run")}
          </button>
          <button className="btn primary" onClick={() => run(false)} disabled={running !== "none"}>
            {running === "live" ? t("Enviando…", "Sending…") : t("Ejecutar campaña", "Run campaign")}
          </button>
          <button className="btn" onClick={load} disabled={loading}>
            {t("Refrescar", "Refresh")}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginTop: 16 }}>
        <StatCard label={t("Total enviados", "Total sent")} value={stats.total} />
        <StatCard label={t("Últimas 24h", "Last 24h")} value={stats.last24h} />
        <StatCard label={t("Últimos 7 días", "Last 7 days")} value={stats.last7d} />
        <StatCard label={t("Últimos 30 días", "Last 30 days")} value={stats.last30d} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginTop: 12 }}>
        <SegmentCard label={t("Segmento 30 días (10% off)", "30-day segment (10% off)")} total={stats.bySegmentTotal["30"]} recent={stats.bySegment30d["30"]} />
        <SegmentCard label={t("Segmento 60 días (15% off)", "60-day segment (15% off)")} total={stats.bySegmentTotal["60"]} recent={stats.bySegment30d["60"]} />
        <SegmentCard label={t("Segmento 90 días (20% off)", "90-day segment (20% off)")} total={stats.bySegmentTotal["90"]} recent={stats.bySegment30d["90"]} />
      </div>

      {lastSummary && (
        <pre style={{ marginTop: 16, background: "#f7f7f7", padding: 12, borderRadius: 8, fontSize: 12, overflowX: "auto" }}>
          {JSON.stringify(lastSummary, null, 2)}
        </pre>
      )}

      <h4 style={{ marginTop: 24 }}>{t("Envíos recientes", "Recent sends")}</h4>
      {loading ? (
        <p>{t("Cargando…", "Loading…")}</p>
      ) : rows.length === 0 ? (
        <p style={{ color: "#888" }}>{t("Aún no hay envíos registrados.", "No sends logged yet.")}</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "1px solid #ddd" }}>
                <th style={{ padding: 8 }}>{t("Fecha", "Date")}</th>
                <th style={{ padding: 8 }}>{t("Segmento", "Segment")}</th>
                <th style={{ padding: 8 }}>{t("Cupón", "Coupon")}</th>
                <th style={{ padding: 8 }}>{t("Usuario", "User")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 100).map(r => (
                <tr key={r.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                  <td style={{ padding: 8 }}>{new Date(r.sent_at).toLocaleString()}</td>
                  <td style={{ padding: 8 }}>{r.segment}d</td>
                  <td style={{ padding: 8 }}><code>{r.coupon_code ?? "—"}</code></td>
                  <td style={{ padding: 8, fontFamily: "monospace", fontSize: 11 }}>{r.user_id.slice(0, 8)}…</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #e5e5e5", borderRadius: 8, padding: 12 }}>
      <div style={{ fontSize: 12, color: "#666" }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, marginTop: 4 }}>{value}</div>
    </div>
  );
}

function SegmentCard({ label, total, recent }: { label: string; total: number; recent: number }) {
  return (
    <div style={{ background: "#fafafa", border: "1px solid #e5e5e5", borderRadius: 8, padding: 12 }}>
      <div style={{ fontSize: 12, color: "#666" }}>{label}</div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 4 }}>
        <div><strong style={{ fontSize: 20 }}>{total}</strong> <span style={{ fontSize: 12, color: "#888" }}>total</span></div>
        <div><strong style={{ fontSize: 16 }}>{recent}</strong> <span style={{ fontSize: 12, color: "#888" }}>30d</span></div>
      </div>
    </div>
  );
}